/**
 * Pure helpers for top-down pre-pack queue navigation.
 * Order of `cartons` must match the API (earliest ship-by, then shipment id, then carton number).
 */

import type { CartonRow } from "./prepackApi";

export function cartonPath(shipmentId: number, cartonNumber: number): string {
  return `/shipping/pre-pack/shipments/${shipmentId}/cartons/${cartonNumber}`;
}

export function transferPath(transferOrderId: number): string {
  return `/shipping/pre-pack/transfers/${String(transferOrderId)}`;
}

function assertCartonRowArray(cartons: CartonRow[]): CartonRow[] {
  return Array.isArray(cartons) ? cartons : [];
}

/** 1-based index in the ordered queue, or 0 if not in the list (for example completed and dropped). */
export function queuePositionOneBased(cartons: CartonRow[], shipmentId: number, cartonNumber: number): number {
  const index = assertCartonRowArray(cartons).findIndex(
    (carton) => carton.shipmentId === shipmentId && carton.cartonNumber === cartonNumber,
  );
  return index >= 0 ? index + 1 : 0;
}

/** Next carton after the current one in the same ordered list, or null if none. */
export function nextCartonAfter(
  cartons: CartonRow[],
  shipmentId: number,
  cartonNumber: number,
): CartonRow | null {
  const rows = assertCartonRowArray(cartons);
  const index = rows.findIndex((carton) => carton.shipmentId === shipmentId && carton.cartonNumber === cartonNumber);
  if (index < 0 || index >= rows.length - 1) return null;
  return rows[index + 1] ?? null;
}

/** Previous carton before the current one, or null if none. */
export function previousCartonBefore(
  cartons: CartonRow[],
  shipmentId: number,
  cartonNumber: number,
): CartonRow | null {
  const rows = assertCartonRowArray(cartons);
  const index = rows.findIndex((carton) => carton.shipmentId === shipmentId && carton.cartonNumber === cartonNumber);
  if (index <= 0) return null;
  return rows[index - 1] ?? null;
}

/** One sentence telling the operator what to do next (top-down within the packing screen). */
export function describeCartonNextStep(carton: CartonRow): string {
  const serialized = carton.items.filter((item) => item.isSerialized);
  const unserialized = carton.items.filter((item) => !item.isSerialized);
  const serializedPending = serialized.some((item) => item.pending > 0);
  const unserializedPending = unserialized.some((item) => item.pending > 0);
  if (serializedPending) {
    return "Next: scan a device label, then the box label, and save pairing. Repeat until no serialized lines are pending.";
  }
  if (unserializedPending) {
    return "Next: type packed quantities for each unserialized line and save. Do that before printing or moving on.";
  }
  if (!carton.isFullyPacked) {
    return "Next: clear the remaining pending counts on the plan above.";
  }
  return "Fully packed on the plan. Next: print the label, then use Open next carton below.";
}
